import { useState } from "react";
import { Lock, Plus, Trash2, CheckCircle2, Clock, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, gbp, fmtDate, startCheckout } from "../lib/api";
import { ReleaseForm, ReleaseProof } from "./JobExtras";

const STATE = {
  unpaid: ["Not paid yet", "bg-slate-100 text-slate-600"],
  held: ["Held securely", "bg-emerald-50 text-emerald-700"],
  requested: ["Awaiting your approval", "bg-amber-50 text-amber-800"],
  on_hold: ["On hold (complaint)", "bg-rose-50 text-rose-700"],
  released: ["Released", "bg-[#374B5C] text-white"],
  refunded: ["Refunded", "bg-slate-200 text-slate-700"],
};

const stateOf = (esc) => !esc ? "unpaid" : esc.status !== "held" ? esc.status : esc.on_hold ? "on_hold" : esc.release_requested_at ? "requested" : "held";

function amounts(stages, total) {
  const out = stages.map((s) => Math.round(total * (+s.percent || 0)) / 100);
  if (out.length) out[out.length - 1] = Math.round((total - out.slice(0, -1).reduce((a, b) => a + b, 0)) * 100) / 100;
  return out;
}

export function StageSplitEditor({ stages, setStages, total }) {
  const sum = stages.reduce((a, s) => a + (+s.percent || 0), 0);
  const amt = amounts(stages, total);
  const set = (i, k, v) => setStages(stages.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  return (
    <div className="mt-3 space-y-2" data-testid="stage-split-editor">
      {stages.map((s, i) => (
        <div key={i} className="grid grid-cols-[1fr_80px_90px_auto] gap-2 items-center">
          <input className="input py-2" value={s.label} onChange={(e) => set(i, "label", e.target.value)} data-testid={`stage-label-${i}`} />
          <div className="relative"><input type="number" className="input py-2 pr-6" value={s.percent} onChange={(e) => set(i, "percent", e.target.value)} data-testid={`stage-percent-${i}`} /><span className="absolute right-2 top-2.5 text-xs muted">%</span></div>
          <span className="text-sm text-right" data-testid={`stage-amount-${i}`}>{gbp(amt[i])}</span>
          <button type="button" onClick={() => setStages(stages.filter((_, j) => j !== i))} disabled={stages.length <= 2} className="btn-ghost btn-sm" aria-label="Remove stage" data-testid={`stage-remove-${i}`}><Trash2 size={14} /></button>
        </div>))}
      <div className="flex justify-between items-center">
        <button type="button" onClick={() => setStages([...stages, { label: `Stage ${stages.length + 1}`, percent: 0 }])} disabled={stages.length >= 5} className="btn-ghost btn-sm" data-testid="stage-add-btn"><Plus size={14} />Add stage</button>
        <span className={`text-xs font-semibold ${Math.abs(sum - 100) < 0.01 ? "text-emerald-700" : "text-rose-600"}`} data-testid="stage-percent-total">Total {sum}% {Math.abs(sum - 100) < 0.01 ? "" : "(must be 100%)"}</span>
      </div>
    </div>
  );
}

export function StageSummary({ stages }) {
  if (!stages?.length) return null;
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3 mt-3 text-sm" data-testid="quote-stage-summary">
      <div className="font-semibold flex items-center gap-1.5 mb-1"><Lock size={13} className="text-emerald-600" />Pay in {stages.length} stages</div>
      {stages.map((s, i) => <div key={i} className="flex justify-between text-xs"><span>{i + 1}. {s.label} ({s.percent}%)</span><span>{gbp(s.amount)}</span></div>)}
    </div>
  );
}

function StageRow({ s, i, esc, children, extra }) {
  const st = stateOf(esc);
  const [label, cls] = STATE[st] || STATE.held;
  return (
    <div className="py-3 border-t border-slate-100 first:border-0" data-testid={`stage-row-${i}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
      <div className="flex-1"><div className="font-semibold text-sm">{i + 1}. {s.label} <span className="muted font-normal">· {s.percent}%</span></div>
        {st === "requested" && <div className="text-xs text-amber-800 flex items-center gap-1 mt-0.5"><Clock size={11} />Released automatically on {fmtDate(esc.auto_release_at)} unless a problem is raised</div>}
        {st === "on_hold" && <div className="text-xs text-rose-700 flex items-center gap-1 mt-0.5"><ShieldAlert size={11} />Paused while the complaint is reviewed</div>}</div>
      <span className="font-bold text-sm" data-testid={`stage-row-amount-${i}`}>{gbp(s.amount)}</span>
      <span className={`chip text-xs px-2.5 py-1 ${cls}`} data-testid={`stage-status-${i}`}>{label}</span>
      {children}
      </div>
      <ReleaseProof esc={esc} />
      {extra}
    </div>
  );
}

const byStage = (list) => Object.fromEntries((list || []).map((e) => [e.stage, e]));

export function HomeStages({ job, provider, days, voucher, onDone }) {
  const [busy, setBusy] = useState(false);
  const paid = byStage(job.stage_payments);
  const next = job.payment_stages.findIndex((_, i) => !paid[i] || ["refunded"].includes(paid[i].status));
  const pay = (i) => startCheckout({ kind: "job_stage", ref: `${job.id}:${i}`, voucher_code: voucher?.code }).catch((e) => toast.error(errMsg(e)));
  const approve = async (i) => {
    if (!window.confirm(`Release ${gbp(job.payment_stages[i].amount)} to ${provider}?`)) return;
    setBusy(true);
    try { await api.post(`/jobs/${job.id}/stages/${i}/approve`); toast.success("Payment released"); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card p-5 border-emerald-200" data-testid="home-stages">
      <h2 className="font-bold flex items-center gap-2"><Lock size={16} className="text-emerald-600" />Stage payments · {gbp(job.agreed_total)}</h2>
      <p className="text-xs muted mt-1 mb-2">Each payment is held securely by MyHomelyHub and only goes to {provider} when you approve it. If they mark a stage done and you don't respond, it's released after {days || 7} days.</p>
      {job.payment_stages.map((s, i) => {
        const st = stateOf(paid[i]);
        return (<StageRow key={i} s={s} i={i} esc={paid[i]}>
          {i === next && job.status !== "cancelled" && <button onClick={() => pay(i)} className="btn-navy btn-sm" data-testid={`stage-pay-btn-${i}`}>Pay {gbp(s.amount)}</button>}
          {["held", "requested"].includes(st) && <button onClick={() => approve(i)} disabled={busy} className="btn-coral btn-sm" data-testid={`stage-approve-btn-${i}`}><CheckCircle2 size={14} />Approve &amp; release</button>}
        </StageRow>);
      })}
    </div>
  );
}

export function ProStages({ job, onDone }) {
  const [open, setOpen] = useState(null);
  const paid = byStage(job.stage_payments);
  return (
    <div className="rounded-xl border border-emerald-200 p-4" data-testid="pro-stages">
      <div className="font-semibold text-sm flex items-center gap-1.5"><Lock size={14} className="text-emerald-600" />Stage payments</div>
      <p className="text-xs muted mt-0.5 mb-1">When a paid stage is done, add photos of the work and ask the homeowner to approve it. If they don't respond it's released to you automatically after the waiting period.</p>
      {job.payment_stages.map((s, i) => (
        <StageRow key={i} s={s} i={i} esc={paid[i]}
          extra={open === i && <ReleaseForm url={`/jobs/${job.id}/stages/${i}/request-release`} testid={`stage-release-form-${i}`} onDone={() => { setOpen(null); onDone(); }} onCancel={() => setOpen(null)} />}>
          {stateOf(paid[i]) === "held" && open !== i && <button onClick={() => setOpen(i)} className="btn-navy btn-sm" data-testid={`stage-request-btn-${i}`}>Stage done, request payment</button>}
        </StageRow>))}
    </div>
  );
}
